import { useMutation, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { reorderBookingTypeRequest } from './booking-type-mutations'
import { BookingCategory, BookingQuestion, BookingType, OrderUpdate } from './booking-types.types'
import { applyOrderUpdates } from './booking-types.utils'
import { USE_GET_BOOKING_CATEGORIES_QUERY_KEY } from './get-booking-categories'
import { USE_GET_BOOKING_QUESTIONS_QUERY_KEY } from './get-booking-questions'
import { USE_GET_BOOKING_TYPES_QUERY_KEY } from './get-booking-types'

const useReorder = <T extends { id: number; order: number }>(
  queryKey: string,
  request: (update: OrderUpdate) => Promise<void>,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: OrderUpdate[]) => Promise.all(updates.map(request)),
    // Note: no refetch on success, a slow response from an earlier drag would overwrite a newer one
    onError: () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: [queryKey] })
      queryClient.setQueryData<T[]>([queryKey], (items) =>
        items ? applyOrderUpdates(items, updates) : items,
      )
    },
  })
}

export const useReorderBookingCategories = () =>
  useReorder<BookingCategory>(USE_GET_BOOKING_CATEGORIES_QUERY_KEY, async ({ id, order }) => {
    await authAPI.put(`/booking-category/${id}`, { order })
  })

export const useReorderBookingTypes = () =>
  useReorder<BookingType>(USE_GET_BOOKING_TYPES_QUERY_KEY, reorderBookingTypeRequest)

export const useReorderBookingQuestions = () =>
  useReorder<BookingQuestion>(USE_GET_BOOKING_QUESTIONS_QUERY_KEY, async ({ id, order }) => {
    await authAPI.put(`/question/${id}`, { order })
  })
